import React, { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Command, CommandEmpty, CommandItem, CommandList } from "./ui/command";
import { cn } from "../lib/utils";

interface AutoSuggestionProps {
  value: string;
  suggestions: string[];
  placeholder?: string;
  onSelect: (value: string) => void;
  onBlur?: (value: string) => void;
  className?: string;
}

const AutoSuggestion: React.FC<AutoSuggestionProps> = ({
  value,
  suggestions,
  placeholder = "Type to search...",
  onSelect,
  onBlur,
  className,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const filteredSuggestions =
    inputValue.trim() === ""
      ? suggestions
      : suggestions.filter((item) =>
        item.toLowerCase().includes(inputValue.trim().toLowerCase())
      );

  const handleSelect = (value: string) => {
    setInputValue(value);
    onSelect(value);
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <Input
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            if (filteredSuggestions.length > 0) {
              handleSelect(filteredSuggestions[0]);
            } else if (inputValue.trim()) {
              // Allow custom value to be selected on Enter
              handleSelect(inputValue.trim());
            }
            e.preventDefault();
          }
        }}
        onBlur={() => {
          // Call onBlur if provided and input has value
          if (onBlur && inputValue.trim()) {
            onBlur(inputValue.trim());
          }
        }}
        className={cn("w-full", className)}
      />
      {open && (
        <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg">
          <Command>
            {filteredSuggestions.length > 0 ? (
              <CommandList>
                {filteredSuggestions.map((item) => (
                  <CommandItem
                    key={item}
                    value={item}
                    onItemSelect={handleSelect}
                    className="cursor-pointer text-start"
                  >
                    {item}
                  </CommandItem>
                ))}
              </CommandList>
            ) : (
              <CommandEmpty>
                <div className="text-xs text-gray-500 p-2">
                  No matching items. Press Enter to use "{inputValue}" as custom.
                </div>
              </CommandEmpty>
            )}
          </Command>
        </div>
      )}
    </div>
  );
};

export default AutoSuggestion;
